import { h, Component } from 'preact';

function niceCeil(value) {
    if (!value || value <= 0) return 1;
    const padded = value * 1.1;
    const magnitude = 10 ** Math.floor(Math.log10(padded));
    return Math.ceil(padded / magnitude) * magnitude;
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

/**
 * Catmull-Rom spline through the points as a cubic-bezier SVG path. Control point y
 * values are clamped to the plot area so a run of zeros never dips below the baseline.
 */
export function smoothPath(points, yMin, yMax) {
    if (points.length === 0) {
        return '';
    }
    if (points.length === 1) {
        return `M${points[0].x},${points[0].y}`;
    }
    let d = `M${points[0].x},${points[0].y}`;
    for (let i = 0; i < points.length - 1; i += 1) {
        const p0 = points[i - 1] || points[i];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + 2] || p2;
        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = clamp(p1.y + (p2.y - p0.y) / 6, yMin, yMax);
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = clamp(p2.y - (p3.y - p1.y) / 6, yMin, yMax);
        d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
    }
    return d;
}

class LineChartCompare extends Component {
    render() {
        const {
            series = [],
            xLabels = [],
            // Optional display text per x label (same length as xLabels). Lets callers keep
            // numeric xLabels (needed for continuous lines) while showing e.g. month names.
            xTickLabels,
            // Show only every n-th x label, counted from the last one so it is always shown.
            xTickEvery = 1,
            // Line styling. Defaults keep existing callers unchanged: 2px polylines, no fill.
            strokeWidth = 2,
            smooth = false,
            area = false,
            fontSize = 10,
            height = 260,
            width: widthProp,
            paddingTop = 16,
            paddingBottom = 36,
            paddingLeft = 32,
            paddingRight = 12,
            yMax: yMaxProp,
        } = this.props;

        // Callers may pin the viewBox width (e.g. a fixed, responsive max); otherwise
        // grow it with the number of x labels.
        const width = widthProp != null ? widthProp : Math.max(280, xLabels.length * 40);
        const innerWidth = width - paddingLeft - paddingRight;
        const innerHeight = height - paddingTop - paddingBottom;

        let max = 0;
        series.forEach(s => {
            s.points.forEach(p => { if (p.y > max) max = p.y; });
        });
        // Optional fixed scale (e.g. yMax=100 for a percent axis); otherwise auto-scale.
        const yMax = yMaxProp != null ? yMaxProp : niceCeil(max);
        const n = xLabels.length;
        const stepX = n > 1 ? innerWidth / (n - 1) : 0;

        const gridSteps = 4;
        const gridLines = [];
        for (let i = 0; i <= gridSteps; i += 1) {
            const y = paddingTop + (innerHeight * i) / gridSteps;
            const labelValue = yMax * (1 - i / gridSteps);
            gridLines.push({ y, labelValue });
        }

        function buildSubLines(points) {
            const sorted = [...points].sort((a, b) => a.x - b.x);
            const groups = [];
            let current = [];
            let prevX = null;
            sorted.forEach(p => {
                if (prevX != null && p.x !== prevX + 1) {
                    if (current.length) groups.push(current);
                    current = [];
                }
                current.push(p);
                prevX = p.x;
            });
            if (current.length) groups.push(current);
            return groups;
        }

        function xPos(xVal) {
            const idx = xLabels.indexOf(xVal);
            return paddingLeft + idx * stepX;
        }
        function yPos(yVal) {
            return paddingTop + innerHeight - (yMax > 0 ? (yVal / yMax) * innerHeight : 0);
        }
        const baseline = paddingTop + innerHeight;
        const markerRadius = strokeWidth > 2 ? strokeWidth + 1 : 3;
        // Series with `dash: true` (e.g. internal use next to sales) are drawn dashed, without area fill.
        const dashArray = `${strokeWidth * 2} ${strokeWidth * 2}`;

        return (
            <div>
                <svg
                    viewBox={`0 0 ${width} ${height}`}
                    preserveAspectRatio='xMidYMid meet'
                    style={`width: 100%; height: ${height}px; max-height: ${height}px;`}
                    role='img'
                >
                    {gridLines.map((line, i) => (
                        <g key={`grid-${i}`}>
                            <line
                                x1={paddingLeft}
                                x2={width - paddingRight}
                                y1={line.y}
                                y2={line.y}
                                stroke='currentColor'
                                stroke-opacity={i === gridSteps ? '0.4' : '0.1'}
                                stroke-width='1'
                            />
                            <text
                                x={paddingLeft - 4}
                                y={line.y + 3}
                                text-anchor='end'
                                font-size={fontSize - 1}
                                fill='currentColor'
                                opacity='0.6'
                            >
                                {Math.round(line.labelValue)}
                            </text>
                        </g>
                    ))}

                    {xLabels.map((lbl, i) => {
                        if ((n - 1 - i) % xTickEvery !== 0) {
                            return null;
                        }
                        return (
                            <text
                                key={`x-${i}`}
                                x={paddingLeft + i * stepX}
                                y={height - 16}
                                text-anchor='middle'
                                font-size={fontSize}
                                fill='currentColor'
                                opacity='0.7'
                            >
                                {xTickLabels && xTickLabels[i] != null ? xTickLabels[i] : lbl}
                            </text>
                        );
                    })}

                    {series.map((s, sIdx) => {
                        const groups = buildSubLines(s.points)
                            .map(g => g.map(p => ({ x: xPos(p.x), y: yPos(p.y) })));
                        return (
                            <g key={`series-${sIdx}`}>
                                {area && !s.dash && groups.map((g, gIdx) => {
                                    if (g.length < 2) {
                                        return null;
                                    }
                                    const line = smooth
                                        ? smoothPath(g, paddingTop, baseline)
                                        : `M${g.map(p => `${p.x},${p.y}`).join(' L')}`;
                                    const first = g[0];
                                    const last = g[g.length - 1];
                                    return (
                                        <path
                                            key={`area-${sIdx}-${gIdx}`}
                                            d={`${line} L${last.x},${baseline} L${first.x},${baseline} Z`}
                                            fill={s.color}
                                            fill-opacity='0.12'
                                            stroke='none'
                                        />
                                    );
                                })}
                                {groups.map((g, gIdx) => (smooth ? (
                                    <path
                                        key={`curve-${sIdx}-${gIdx}`}
                                        d={smoothPath(g, paddingTop, baseline)}
                                        fill='none'
                                        stroke={s.color}
                                        stroke-width={strokeWidth}
                                        stroke-dasharray={s.dash ? dashArray : undefined}
                                        stroke-linejoin='round'
                                        stroke-linecap='round'
                                    />
                                ) : (
                                    <polyline
                                        key={`poly-${sIdx}-${gIdx}`}
                                        points={g.map(p => `${p.x},${p.y}`).join(' ')}
                                        fill='none'
                                        stroke={s.color}
                                        stroke-width={strokeWidth}
                                        stroke-dasharray={s.dash ? dashArray : undefined}
                                        stroke-linejoin='round'
                                        stroke-linecap='round'
                                    />
                                )))}
                                {s.points.map((p, pIdx) => (
                                    <circle
                                        key={`pt-${sIdx}-${pIdx}`}
                                        cx={xPos(p.x)}
                                        cy={yPos(p.y)}
                                        r={s.dash ? Math.max(2, markerRadius - 2) : markerRadius}
                                        fill={s.color}
                                        stroke={strokeWidth > 2 ? 'var(--bs-body-bg)' : 'none'}
                                        stroke-width={strokeWidth > 2 ? 2 : 0}
                                    />
                                ))}
                            </g>
                        );
                    })}
                </svg>
                <div class='d-flex flex-wrap justify-content-center gap-3 mt-2'>
                    {series.map((s, sIdx) => (
                        <div class='d-flex align-items-center gap-1' key={`leg-${sIdx}`}>
                            {s.dash ? (
                                <span
                                    class='legend-dash'
                                    style={`display:inline-block;width:16px;height:0;border-top:2px dashed ${s.color};`}
                                />
                            ) : (
                                <span style={`display:inline-block;width:12px;height:12px;background:${s.color};border-radius:2px;`} />
                            )}
                            <small>{s.label}</small>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

export default LineChartCompare;
